import { Box, Stack, SxProps, Theme } from '@mui/material';
import { ReactNode, useEffect, useRef } from 'react';

import { BaseChatMessage, RenderChatMessage } from '../types';

const DEFAULT_MESSAGE_GAP = 2;

export interface ChatTranscriptProps<TMessage extends BaseChatMessage> {
  alignToBottom?: boolean;
  contentSx?: SxProps<Theme>;
  errorState?: ReactNode;
  getMessageGap?: (
    message: TMessage,
    previousMessage: TMessage
  ) => number | undefined;
  loadingState?: ReactNode;
  messages: TMessage[];
  onScrollTargetReached?: () => void;
  renderMessage: RenderChatMessage<TMessage>;
  scrollToMessageId?: string | null;
}

export function ChatTranscript<TMessage extends BaseChatMessage>({
  alignToBottom = true,
  contentSx,
  errorState,
  getMessageGap,
  loadingState,
  messages,
  onScrollTargetReached,
  renderMessage,
  scrollToMessageId,
}: ChatTranscriptProps<TMessage>) {
  const scrollTargetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!scrollToMessageId || !scrollTargetRef.current) {
      return;
    }

    scrollTargetRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    onScrollTargetReached?.();
  }, [onScrollTargetReached, scrollToMessageId]);

  return (
    <Stack
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: alignToBottom ? '100%' : undefined,
      }}
    >
      {alignToBottom && <Stack sx={{ flexGrow: 1 }} />}
      <Stack
        sx={{
          gap: DEFAULT_MESSAGE_GAP,
          px: 2,
          py: 1,
          ...contentSx,
        }}
      >
        {messages.map((message, index) => {
          const previousMessage = messages[index - 1];
          const messageGap = previousMessage
            ? getMessageGap?.(message, previousMessage)
            : undefined;

          return (
            // Lay each row out as a flex column so a message's own `alignSelf`
            // is honored: user bubbles align right (flex-end) and assistant
            // messages align left (flex-start). A plain block wrapper silently
            // drops `alignSelf`, which pinned every bubble to the left.
            <Box
              key={message.id}
              ref={
                message.id === scrollToMessageId ? scrollTargetRef : undefined
              }
              sx={{
                display: 'flex',
                flexDirection: 'column',
                // Offset the default Stack gap when this message pair needs a
                // different distance. Status rows keep the default gap.
                mt:
                  messageGap === undefined
                    ? undefined
                    : messageGap - DEFAULT_MESSAGE_GAP,
              }}
            >
              {renderMessage({ message })}
            </Box>
          );
        })}
        {loadingState}
        {errorState}
      </Stack>
    </Stack>
  );
}
